import type {
  SteelOrderQualityCheckStatusEnum,
  SteelOrderSortInput,
  SteelOrderSortKeyEnum,
  SteelOrdersSearchInput,
} from '@mbfab/client-api'
import type { ShapeNameConversionDisplayMap } from '@mbfab/shared'
import { isPresent, isSome } from '@mbfab/shared'
import { Ban, CalendarCog, Printer, RotateCcw, Trash2 } from 'lucide-react'
import type { ReactNode } from 'react'
import { useState } from 'react'

import { ColumnHelp, columns, REFERENCE_NUMBER_HELP, renderCell } from './orders-table-columns'
import { OutputDateDialog } from './output-date-dialog'
import { PrintDialog } from './print-dialog'
import type { PrintDialogState, S3ObjectForPrint, SteelOrderForPrint } from './print-types'
import { QUALITY_CHECK_STATUS_CELL_WIDTH_CLASS, QualityCheckStatusCell } from './quality-check-status-cell'
import type { SteelOrderViewModel } from './types'

import { DataTableLink } from '@/components/app/data-table-link'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { MAX_PRINT_SELECTION_COUNT, printSelectionLimitMessage } from '@/components/ui/data-table/bulk-operation-limits'
import type {
  DataTableColumn,
  DataTableExpandable,
  DataTableRowSelection,
  DataTableSorter,
} from '@/components/ui/data-table/data-table'
import { DataTable } from '@/components/ui/data-table/data-table'
import { SelectAllResultsBanner } from '@/components/ui/data-table/select-all-banner'
import { useTableSelection } from '@/components/ui/data-table/use-table-selection'
import { valueOrDash } from '@/components/ui/data-table/value-or-dash'
import { ResultBanner } from '@/components/ui/result-banner'
import { TooltipProvider } from '@/components/ui/tooltip'
import { notify } from '@/lib/notify'
import { RETRY_HINT, runMutation } from '@/lib/run-mutation'

// 発注一覧のソート実行はバックエンド（steelOrders query の sorts 変数）。
// DataTable の sorters は列 key を汎用の string として保持するため、GraphQL へ渡す直前に
// SteelOrderSortKeyEnum へ安全に絞り込む（as any/unknown 禁止のため型ガード関数で判定する）。
// orders-table-columns.tsx の columns 定義はソート可能列の key を SteelOrderSortKeyEnum の値と
// 一致させているため（例: 'category' 列の key が 'category'）、通常はここで絞り込まれることはない。
// Record<SteelOrderSortKeyEnum, true> にすることで、スキーマ側に列挙値が増えたときに
// このマップの更新漏れがコンパイルエラーになる（Set<SteelOrderSortKeyEnum>([...]) は部分集合でも
// コンパイルが通ってしまい、漏れに気づけなかったため）
const STEEL_ORDER_SORT_KEY_MAP = {
  category: true,
  importedAt: true,
  millsheetStatus: true,
  millsheetWeight: true,
  orderStatus: true,
  orderedWeight: true,
  outputDate: true,
  qualityCheckStatus: true,
  referenceNumber: true,
  supplierName: true,
  totalWeight: true,
} as const satisfies Record<SteelOrderSortKeyEnum, true>
const STEEL_ORDER_SORT_KEYS: ReadonlySet<string> = new Set(Object.keys(STEEL_ORDER_SORT_KEY_MAP))

function isSteelOrderSortKey(key: string): key is SteelOrderSortKeyEnum {
  return STEEL_ORDER_SORT_KEYS.has(key)
}

// route（コンテナ）側で DataTableSorter[] から GraphQL の sorts 変数へ変換するためのヘルパー
export function toSteelOrderSorts(sorters: readonly DataTableSorter[]): SteelOrderSortInput[] {
  return sorters
    .filter((sorter): sorter is DataTableSorter & { key: SteelOrderSortKeyEnum } => isSteelOrderSortKey(sorter.key))
    .map((sorter) => ({ key: sorter.key, order: sorter.order }))
}

// 一括操作・出力日編集の実行結果。成功はトーストのみで伝え、失敗理由だけ DOM に残す
// （2026/08/30 FB「結果パネルは基本消してほしい。toast で十分」。
// docs/develop/client_ui/screens/states.md「操作の結果は「成功はトースト、失敗はトーストと画面の両方」」）
type BulkFeedback = { type: 'error'; message: string }

export type SteelOrderBulkTarget = { ids: string[] } | { filter: SteelOrdersSearchInput }

type SteelOrderItem = SteelOrderViewModel['items'][number]

// 長文になり得る建築備考は DataTable の既定（1行で省略し、全文はホバーで出す）に任せる。
// 詳細画面（items-table.tsx）は逆に折り返して全文を出す
function buildItemColumns(
  order: SteelOrderViewModel,
  onUpdateItemQualityCheckStatus: (
    steelOrderItemId: string,
    qualityCheckStatus: SteelOrderQualityCheckStatusEnum,
  ) => Promise<boolean>,
): Array<DataTableColumn<SteelOrderItem>> {
  return [
    {
      key: 'managementNumber',
      title: '管理番号',
      minWidthClass: 'min-w-[128px]',
      render: (item): ReactNode => valueOrDash(item.managementNumber),
    },
    {
      key: 'constructionNumber',
      title: '工事番号',
      minWidthClass: 'min-w-[128px]',
      render: (item): ReactNode => valueOrDash(item.constructionNumber),
    },
    {
      key: 'constructionName',
      title: '工事名称',
      minWidthClass: 'min-w-[220px]',
      render: (item): ReactNode => valueOrDash(item.constructionName),
    },
    {
      key: 'productName',
      title: '品名',
      minWidthClass: 'min-w-[176px]',
      render: (item): ReactNode => valueOrDash(item.productName),
    },
    {
      key: 'standard',
      title: '規格',
      minWidthClass: 'min-w-[112px]',
      render: (item): ReactNode => valueOrDash(item.standard),
    },
    {
      key: 'size',
      title: 'サイズ',
      minWidthClass: 'min-w-[176px]',
      render: (item): ReactNode => valueOrDash(item.size),
    },
    {
      key: 'length',
      title: '長さ',
      minWidthClass: 'min-w-[96px]',
      render: (item): ReactNode => valueOrDash(item.length),
    },
    {
      key: 'quantity',
      title: '員数',
      numeric: true,
      minWidthClass: 'min-w-[80px]',
      render: (item): ReactNode => valueOrDash(item.quantity),
    },
    {
      key: 'weight',
      title: '重量(kg)',
      numeric: true,
      minWidthClass: 'min-w-[112px]',
      render: (item): ReactNode => valueOrDash(item.weight?.toLocaleString()),
    },
    {
      key: 'manufacturerName',
      title: '製造会社名',
      minWidthClass: 'min-w-[160px]',
      render: (item): ReactNode => valueOrDash(item.manufacturerName),
    },
    {
      key: 'itemNumber',
      title: '品目番号',
      minWidthClass: 'min-w-[144px]',
      render: (item): ReactNode => valueOrDash(item.itemNumber),
    },
    {
      key: 'answeredDeliveryDate',
      title: '回答納入日',
      minWidthClass: 'min-w-[144px]',
      render: (item): ReactNode => valueOrDash(item.answeredDeliveryDate),
    },
    {
      key: 'constructionNote',
      title: '建築備考',
      minWidthClass: 'min-w-[200px]',
      render: (item): ReactNode => valueOrDash(isPresent(item.constructionNote) ? item.constructionNote : null),
    },
    {
      key: 'qualityCheckStatus',
      title: '品管照合ステータス',
      minWidthClass: QUALITY_CHECK_STATUS_CELL_WIDTH_CLASS,
      render: (item): ReactNode => (
        <QualityCheckStatusCell
          referenceNumber={order.referenceNumber}
          item={item}
          onUpdateItemQualityCheckStatus={onUpdateItemQualityCheckStatus}
        />
      ),
    },
  ]
}

export function OrdersTable({
  orders,
  loading,
  pagination,
  sorters,
  onChangeSorters,
  onChangePage,
  onBulkExclude,
  onBulkInclude,
  onUpdateOutputDate,
  onUpdateItemQualityCheckStatus,
  onBulkDelete,
  shapeNameConversionMap,
  onFetchPrintOrders,
  onFetchPrintImages,
  defaultOutputDate,
  filter,
  conditionsKey,
  onCountAllResults,
  onFetchAllResultIdsForPrint,
  actionsDisabled = false,
}: {
  orders: SteelOrderViewModel[]
  loading: boolean
  pagination: { page: number; pageSize: number; totalCount: number }
  // 複数列ソート（DataTable 共通の型）。ソート実行はバックエンドのため、変換は route 側で
  // toSteelOrderSorts を使って行う
  sorters: DataTableSorter[]
  onChangeSorters: (sorters: DataTableSorter[]) => void
  onChangePage: (page: number) => void
  // 一括操作・セル編集はいずれも updateSteelOrders(ids, ...) / deleteSteelOrders(ids) の薄いラップで、
  // mutation 自体・refetch は route（コンテナ）側の責務にする（画面コンポーネントに GraphQL を持ち込まない）
  onBulkExclude: (target: SteelOrderBulkTarget) => Promise<boolean>
  onBulkInclude: (target: SteelOrderBulkTarget) => Promise<boolean>
  onUpdateOutputDate: (target: SteelOrderBulkTarget, outputDate: string | null) => Promise<boolean>
  // 明細展開行の品管照合ステータス更新。updateSteelOrderItemQualityCheckStatus mutation の薄いラップ
  // （発注詳細画面の SteelOrderItemsTable と同じ関数シグネチャ・同じ mutation を共有する）
  onUpdateItemQualityCheckStatus: (itemId: string, status: SteelOrderQualityCheckStatusEnum) => Promise<boolean>
  onBulkDelete: (target: SteelOrderBulkTarget) => Promise<boolean>
  // 印刷ダイアログ用。表示用のミルシート名寄せ（v1 と同じ変換マスタ）と、選択∩表示中のidsで
  // 明細・PDF情報まで取り直す2つのフェッチャー（route側でclient.fetchSteelOrdersForPrint /
  // client.fetchS3Objectsを呼ぶ薄いラップ。画面コンポーネントはGraphQLを直接呼ばない）
  shapeNameConversionMap: ShapeNameConversionDisplayMap
  onFetchPrintOrders: (ids: string[]) => Promise<SteelOrderForPrint[]>
  onFetchPrintImages: (s3ObjectIds: string[]) => Promise<S3ObjectForPrint[]>
  // 出力日一括変更ダイアログの初期値（既定は「今日」）。Storybook から固定日を渡して
  // VRT が日をまたいでも安定するようにするための口（実アプリでは指定しない）
  defaultOutputDate?: string
  filter: SteelOrdersSearchInput
  conditionsKey: string
  onCountAllResults: (filter: SteelOrdersSearchInput) => Promise<number | null>
  onFetchAllResultIdsForPrint: (filter: SteelOrdersSearchInput) => Promise<string[] | null>
  // 絞り込みの入力待ち中など、表示中の一覧が条件と一致していない間は一括操作を止める
  actionsDisabled?: boolean
}) {
  const [printState, setPrintState] = useState<PrintDialogState>({ status: 'closed' })
  const [outputDateDialogOpen, setOutputDateDialogOpen] = useState(false)
  const [excludeDialogOpen, setExcludeDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [bulkFeedback, setBulkFeedback] = useState<BulkFeedback | null>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  // 一括操作の二重送信ガード。特に印刷は連打すると並列フェッチになるため、進行中は
  // ツールバーの一括操作ボタンを丸ごと disabled にする（docs/develop/client_ui/ui_verification.md「操作結果を機械可読で確定できるようにする」
  // と同じ思想で、連打による多重実行を防ぐ）
  const [bulkPending, setBulkPending] = useState(false)
  const [confirmedCount, setConfirmedCount] = useState(0)
  const [confirmedTarget, setConfirmedTarget] = useState<SteelOrderBulkTarget | null>(null)
  const [includeDialogOpen, setIncludeDialogOpen] = useState(false)
  const pageIds = orders.map((order) => order.id)
  const {
    selection,
    selectedCount,
    allPageSelected,
    somePageSelected,
    isSelected,
    toggleSelectAllPage,
    toggleSelect,
    selectAllResults,
    clearSelection,
  } = useTableSelection({ pageIds, filter, filterKey: conditionsKey, totalCount: pagination.totalCount })
  const actionTarget: SteelOrderBulkTarget =
    selection.mode === 'all' ? { filter: selection.filter } : { ids: selection.ids }

  const toggleExpanded = (id: string) => {
    setExpanded((previous) => {
      const next = new Set(previous)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const countActionTarget = async (target: SteelOrderBulkTarget): Promise<number | null> => {
    if ('ids' in target) {
      return target.ids.length
    }
    setBulkPending(true)
    const count = await onCountAllResults(target.filter)
    setBulkPending(false)
    if (!isSome(count)) {
      notify.error('対象件数の確認に失敗しました', RETRY_HINT)
      setBulkFeedback({ type: 'error', message: '対象件数の確認に失敗しました' })
      return null
    }
    return count
  }

  const openCountedDialog = async (open: (value: boolean) => void) => {
    // count待機中に選択やfilterが変わっても、確認した件数とmutation対象を同じsnapshotに固定する。
    const target = actionTarget
    const count = await countActionTarget(target)
    if (isSome(count)) {
      setConfirmedCount(count)
      setConfirmedTarget(target)
      open(true)
    }
  }

  // 「対象外にする」は確認ダイアログ経由（2026/08/26 UX方針決定）。「対象外を解除」は復元操作で
  // 取り消しが効くため、削除と違い確認なしの即実行のまま据え置く
  // onBulkExclude 等は route（コンテナ）側で既にエラーを握りつぶし boolean で返す設計。
  // runMutation は false に解決した mutate も失敗として扱うため、そのまま渡せる
  const handleExcludeConfirm = async () => {
    if (!isSome(confirmedTarget)) {
      return
    }
    const target = confirmedTarget
    const count = confirmedCount
    setExcludeDialogOpen(false)
    setConfirmedTarget(null)
    setBulkPending(true)
    await runMutation({
      mutate: async () => await onBulkExclude(target),
      success: { message: `${count}件の発注を対象外にしました` },
      failure: { message: '更新に失敗しました' },
      onResult: setBulkFeedback,
    })
    setBulkPending(false)
  }

  const handleBulkInclude = async (target: SteelOrderBulkTarget, count: number) => {
    setIncludeDialogOpen(false)
    setConfirmedTarget(null)
    setBulkPending(true)
    await runMutation({
      mutate: async () => await onBulkInclude(target),
      success: { message: `${count}件の発注の対象外を解除しました` },
      failure: { message: '更新に失敗しました' },
      onResult: setBulkFeedback,
    })
    setBulkPending(false)
  }

  const handleOutputDateConfirm = async (outputDate: string | null) => {
    if (!isSome(confirmedTarget)) {
      return
    }
    const target = confirmedTarget
    const count = confirmedCount
    setOutputDateDialogOpen(false)
    setConfirmedTarget(null)
    setBulkPending(true)
    await runMutation({
      mutate: async () => await onUpdateOutputDate(target, outputDate),
      success: { message: `${count}件の発注の出力日を更新しました` },
      failure: { message: '出力日の更新に失敗しました' },
      onResult: setBulkFeedback,
    })
    setBulkPending(false)
  }

  const handleDeleteConfirm = async () => {
    if (!isSome(confirmedTarget)) {
      return
    }
    const target = confirmedTarget
    const count = confirmedCount
    setDeleteDialogOpen(false)
    setConfirmedTarget(null)
    setBulkPending(true)
    const ok = await runMutation({
      mutate: async () => await onBulkDelete(target),
      success: { message: `${count}件の発注を削除しました` },
      failure: { message: '削除に失敗しました' },
      onResult: setBulkFeedback,
    })
    setBulkPending(false)
    if (ok) {
      // v1 と同じく、削除成功時だけ選択を解除する（対象外・出力日変更は連続操作しやすいよう選択を残す）
      clearSelection()
    }
  }

  // 「印刷」ボタン押下時に選択∩表示中のidsで明細・PDF情報を取り直す。取得完了までダイアログは
  // ローディング状態で開いておく（PrintDialog は取得済みデータを props で受け取るだけの設計。
  // print-dialog.tsx 冒頭のコメント参照）。useEffect ではなくボタンのクリックハンドラで
  // 明示的に発火させることで、フェッチャー props の参照が毎レンダー変わっても再取得ループが起きない
  const handleOpenPrint = async () => {
    const count = await countActionTarget(actionTarget)
    if (!isSome(count)) {
      return
    }
    if (count > MAX_PRINT_SELECTION_COUNT) {
      notify.error(printSelectionLimitMessage())
      setBulkFeedback({ type: 'error', message: printSelectionLimitMessage() })
      return
    }
    const ids = selection.mode === 'ids' ? selection.ids : await onFetchAllResultIdsForPrint(selection.filter)
    if (!isSome(ids)) {
      notify.error('印刷対象の取得に失敗しました', RETRY_HINT)
      setBulkFeedback({ type: 'error', message: '印刷対象の取得に失敗しました' })
      return
    }
    if (ids.length > MAX_PRINT_SELECTION_COUNT) {
      notify.error(printSelectionLimitMessage())
      setBulkFeedback({ type: 'error', message: printSelectionLimitMessage() })
      return
    }
    setBulkPending(true)
    setPrintState({ status: 'loading' })
    try {
      const printOrders = await onFetchPrintOrders(ids)
      const s3ObjectIds = printOrders.flatMap((order) =>
        order.millsheets.flatMap((millsheet) => millsheet.s3Objects.map((s3Object) => s3Object.id)),
      )
      const images = await onFetchPrintImages(s3ObjectIds)
      setPrintState({ status: 'ready', orders: printOrders, images })
    } catch {
      setPrintState({ status: 'error' })
    } finally {
      setBulkPending(false)
    }
  }

  const totalPages = Math.max(1, Math.ceil(pagination.totalCount / pagination.pageSize))

  // 列定義。参照番号は v1 SteelOrdersTable と同じく左固定の別列として扱う（表示列メニューの対象外）。
  // 残り17列は orders-table-columns.tsx の columns（ColumnDef[]）から生成する
  const dataTableColumns: Array<DataTableColumn<SteelOrderViewModel>> = [
    {
      key: 'referenceNumber',
      title: '参照番号',
      // v1 REFERENCE_NUMBER_HELP（2026/08/03 FB: 「参照番号」だけでは何の番号か分からない）
      help: <ColumnHelp title="参照番号" help={REFERENCE_NUMBER_HELP} />,
      sortable: true,
      fixed: 'left',
      keyColumn: true,
      minWidthClass: 'min-w-40',
      render: (order) => (
        // 発注詳細への導線（v1 と同じ実ルートへの遷移）。params は関数形式で渡し、現在の
        // tenant_id を引き継ぐ（このテーブルは /t/$tenant_id/order_matching/ 配下でしか使わないため、
        // tenantId を props で受け取らずに済む）
        <DataTableLink
          to="/t/$tenant_id/order_matching/$steel_order_id"
          // from を明示すると、このリンクが常に一覧route配下でレンダーされることをTSに伝えられ、
          // params 関数の prev が「tenant_id 必須」に絞り込まれる
          from="/t/$tenant_id/order_matching/"
          params={(prev) => ({ ...prev, steel_order_id: order.id })}
          data-testid={`steel-order-link-${order.referenceNumber}`}
        >
          {order.referenceNumber}
        </DataTableLink>
      ),
    },
    ...columns.map((col): DataTableColumn<SteelOrderViewModel> => ({
      key: col.key,
      title: col.label,
      numeric: col.numeric,
      minWidthClass: col.minWidth,
      sortable: isSome(col.sortKey),
      hidable: true,
      help: isSome(col.help) ? <ColumnHelp title={col.label} help={col.help} /> : undefined,
      // renderCell の戻り値型 ReactNode は React 19 で Promise<AwaitedReactNode> を含む Union になり、
      // 明示の戻り値型注釈が無いと @typescript-eslint/promise-function-async が誤検知する
      // （renderCell 自体が Promise を返すわけではない）。注釈を付けて回避する
      render: (order): ReactNode =>
        renderCell(col, order, {
          onUpdateOutputDate: async (ids, outputDate) => await onUpdateOutputDate({ ids }, outputDate),
        }),
    })),
  ]

  const rowSelection: DataTableRowSelection<SteelOrderViewModel> = {
    isSelected: (order) => isSelected(order.id),
    allPageSelected,
    somePageSelected,
    onToggleSelectAllPage: toggleSelectAllPage,
    onToggleRow: (order) => {
      toggleSelect(order.id)
    },
    rowLabel: (order) => `${order.referenceNumber}を選択`,
  }

  const expandable: DataTableExpandable<SteelOrderViewModel> = {
    isExpanded: (order) => expanded.has(order.id),
    onToggle: (order) => {
      toggleExpanded(order.id)
    },
    expandedRowTitle: (order) => (
      <>
        {order.referenceNumber} の発注明細
        <span className="ml-2 text-xs font-medium text-muted-foreground">{order.items.length}件</span>
      </>
    ),
    expandedRowRender: (order) => (
      // 列順は発注詳細の明細テーブル（items-table.tsx）に合わせ、品管照合ステータスは最右を維持する
      // （20260907_01 追加実装）。管理番号・工事番号・工事名称は items-table.tsx には無い列だが、
      // 親行側の集約列（カンマ区切り）とは別に「この明細は具体的にどれか」を一覧だけで判別できるよう
      // 先頭（識別列）に置く。品管照合ステータス列の幅は quality-check-status-cell.tsx の
      // QUALITY_CHECK_STATUS_CELL_WIDTH_CLASS（バッジ+更新日時+セレクト+更新失敗表示がはみ出さない幅）を使う
      <DataTable
        columns={buildItemColumns(order, onUpdateItemQualityCheckStatus)}
        rows={order.items}
        rowKey={(item) => item.id}
        testIdPrefix="steel-order-items-table"
        // 見出しだけのテーブルは「値が無い」と「描画されていない」の区別が付かない
        emptyMessage="発注明細がありません"
      />
    ),
    detailsId: (order) => `order-details-${order.id}`,
    // 展開トグルの aria-label は従来どおり参照番号ベースを維持する（rowKey は React key 専用に切り離す）
    rowLabel: (order) => order.referenceNumber,
  }

  return (
    // 列ヘッダーのヘルプツールチップ（多数のヘッダーに付く）で hover 遅延の挙動を揃えるため、
    // テーブル全体を1つの TooltipProvider で包む
    <TooltipProvider>
      <DataTable
        columns={dataTableColumns}
        rows={orders}
        // React key は id を使う（rowLabel 分離に合わせて4画面のテーブルで統一。DataTable が
        // rowKey を React key と展開トグルの aria-label 生成元に兼用していたため、業務キーの
        // 重複可能性に関わらず React key は技術的なユニークキー（id）に寄せる）。
        // 行 testid・展開トグルの aria-label は従来どおり参照番号ベースを維持する
        rowKey={(order) => order.id}
        rowTestId={(order) => `steel-order-row-${order.referenceNumber}`}
        testIdPrefix="steel-order"
        loading={loading}
        emptyMessage="該当する発注がありません"
        sorters={sorters}
        onChangeSorters={onChangeSorters}
        rowSelection={rowSelection}
        expandable={expandable}
        selectionActions={
          <>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              data-testid="steel-order-bulk-exclude"
              disabled={bulkPending || actionsDisabled}
              onClick={() => {
                void openCountedDialog(setExcludeDialogOpen)
              }}
            >
              <Ban className="size-3.5" />
              対象外にする
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              data-testid="steel-order-bulk-include"
              disabled={bulkPending || actionsDisabled}
              onClick={() => {
                if (selection.mode === 'all') {
                  void openCountedDialog(setIncludeDialogOpen)
                  return
                }
                void handleBulkInclude({ ids: selection.ids }, selection.ids.length)
              }}
            >
              <RotateCcw className="size-3.5" />
              対象外を解除
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              data-testid="steel-order-bulk-output-date"
              disabled={bulkPending || actionsDisabled}
              onClick={() => {
                void openCountedDialog(setOutputDateDialogOpen)
              }}
            >
              <CalendarCog className="size-3.5" />
              出力日を一括変更
            </Button>
            <Button
              size="sm"
              className="gap-1.5"
              data-testid="steel-order-bulk-print"
              disabled={bulkPending || actionsDisabled}
              onClick={() => {
                void handleOpenPrint()
              }}
            >
              <Printer className="size-3.5" />
              印刷
            </Button>
            <div className="mx-1 h-5 w-px bg-border" aria-hidden="true" />
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 text-destructive hover:bg-destructive/10 hover:text-destructive"
              data-testid="steel-order-bulk-delete"
              disabled={bulkPending || actionsDisabled}
              onClick={() => {
                void openCountedDialog(setDeleteDialogOpen)
              }}
            >
              <Trash2 className="size-3.5" />
              削除
            </Button>
          </>
        }
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        banners={
          <>
            {isSome(bulkFeedback) && (
              <ResultBanner
                status={bulkFeedback.type}
                message={bulkFeedback.message}
                testId="steel-order-bulk-result"
                variant="strip"
                onDismiss={() => {
                  setBulkFeedback(null)
                }}
              />
            )}

            {/* ページ跨ぎの選択は「全 N 件を選択」バナー経由でのみ発生させる（2026/08/26 FB）。
              このファイルの allSelected は「表示中ページ全選択」の意味 */}
            {totalPages > 1 && allPageSelected && (
              <SelectAllResultsBanner
                entityLabel="発注"
                totalCount={pagination.totalCount}
                pageCount={orders.length}
                selectionMode={selection.mode}
                onSelectAllResults={selectAllResults}
                onClearSelection={clearSelection}
                testIdPrefix="steel-order"
              />
            )}
          </>
        }
        pagination={{
          page: pagination.page,
          pageSize: pagination.pageSize,
          totalCount: pagination.totalCount,
          onChangePage,
          // 1ページ50〜100行あり、下端までスクロールしないとページを送れないため上部にも出す
          showAtTop: true,
        }}
      />

      <OutputDateDialog
        open={outputDateDialogOpen}
        selectedCount={confirmedCount}
        defaultOutputDate={defaultOutputDate}
        onClose={() => {
          setOutputDateDialogOpen(false)
          setConfirmedTarget(null)
        }}
        onConfirm={(outputDate) => {
          void handleOutputDateConfirm(outputDate)
        }}
      />
      <ConfirmDialog
        open={excludeDialogOpen}
        title="発注を対象外にする"
        message={`選択中の${confirmedCount}件の発注を対象外にします。対象外にした発注は「対象外を解除」でいつでも元に戻せます。`}
        tone="neutral"
        icon={<Ban className="size-4" />}
        confirmLabel="対象外にする"
        confirmTestId="steel-order-exclude-confirm"
        onClose={() => {
          setExcludeDialogOpen(false)
          setConfirmedTarget(null)
        }}
        onConfirm={() => {
          void handleExcludeConfirm()
        }}
      />
      <ConfirmDialog
        open={includeDialogOpen}
        title="発注の対象外を解除する"
        message={`選択中の${confirmedCount}件の発注の対象外を解除します。`}
        tone="neutral"
        icon={<RotateCcw className="size-4" />}
        confirmLabel="対象外を解除"
        confirmTestId="steel-order-include-confirm"
        onClose={() => {
          setIncludeDialogOpen(false)
          setConfirmedTarget(null)
        }}
        onConfirm={() => {
          if (isSome(confirmedTarget)) {
            void handleBulkInclude(confirmedTarget, confirmedCount)
          }
        }}
      />
      <ConfirmDialog
        open={deleteDialogOpen}
        title="発注データの削除"
        message={`選択中の${confirmedCount}件の発注データ（明細・取込済みの元データを含む）を削除します。紐付いていた ミルシートは未紐付けに戻ります。この操作は取り消せません。よろしいですか？`}
        confirmTestId="steel-order-delete-confirm"
        onClose={() => {
          setDeleteDialogOpen(false)
          setConfirmedTarget(null)
        }}
        onConfirm={() => {
          void handleDeleteConfirm()
        }}
      />

      <PrintDialog
        open={printState.status !== 'closed'}
        state={printState}
        selectedCount={selectedCount}
        shapeNameConversionMap={shapeNameConversionMap}
        onClose={() => {
          setPrintState({ status: 'closed' })
        }}
      />
    </TooltipProvider>
  )
}
